import React, { useCallback, useMemo, useState } from 'react';
import { useGlobalContext, useRenderCount, useAirportOptionCache, SearchFieldEnum, } from '@honeycomb/data';
import { Autocompleter, AutocompleterCategory, AutocompleterField, AutocompleterList, AutocompleterOption, AutocompleterResults, ButtonField, LocationIcon, PlaneTakeOffIcon, Chip, PopoverAnchor, useHoneycombTheme, usePrevious, Anchor, } from '@honeycomb/ui-core';
import { getSelectedOptionSummary } from '../../../utils/getSelectedOptionSummary';
import { SearchDisplayType, useSearchContext } from '../SearchContext';
import { SearchDispatchActionType } from '../SearchReducer';
import { FieldDialog } from '../components/FieldDialog';
import { AutocompleterSkeleton } from '../components/AutocompleterSkeleton';
import { AutocompleterNoMatches } from '../components/AutocompleterNoMatches';
import { FieldErrorMessage } from '../components/FieldErrorMessage';
import { pluraliseCount } from '../../../utils/pluraliseCount';
import { SearchFieldPopover } from '../components/SearchFieldPopover/SearchFieldPopover';
import { SearchFieldPopoverPanel } from '../components/SearchFieldPopover/SearchFieldPopoverPanel';
import { SEARCH_FIELD_POPOVER_INNER_PADDING, SEARCH_FIELD_TRANSITION } from '../../../utils';
const flattenAutocompleterValue = (value) => {
    if (Array.isArray(value)) {
        // Type safe cast here as types sufficiently overlap
        return value.reduce((acc, el) => (Array.isArray(el) ? [...acc, ...el] : [...acc, el]), []);
    }
    return [value];
};
const renderSingleOption = (option, inset, removeable = false) => {
    const { code, title, country } = option;
    return (React.createElement(AutocompleterOption, { key: `option-${code}`, id: `option-${code}`, value: code, title: title, subtitle: country, appendix: code, inset: inset, icon: React.createElement(PlaneTakeOffIcon, { id: `flight_icon_${code}` }), action: removeable ? 'remove' : 'default' }));
};
const renderGroupOption = (option) => {
    const { country, title, codes, subAirports } = option;
    const groupSubAirports = subAirports === null || subAirports === void 0 ? void 0 : subAirports.filter((airport) => !!airport);
    const children = groupSubAirports.map((airport) => renderSingleOption(airport, true)) || [];
    if (children.length === 0) {
        return null;
    }
    return (React.createElement(AutocompleterOption, { key: `option-${codes === null || codes === void 0 ? void 0 : codes.join(':')}`, id: `option-${codes === null || codes === void 0 ? void 0 : codes.join(':')}`, value: codes || [], title: title, subtitle: country, appendix: "", inset: false, icon: React.createElement(LocationIcon, { id: `map_icon_${(codes || []).join('_')}` }) },
        React.createElement(AutocompleterList, { subList: true }, children)));
};
const POPOVER_WIDTH = 400;
export function AirportAutocompleter({ state, actions }) {
    var _a, _b;
    const { data, previousData, loading, searchClicked, error: errorProp, popupPlacement = 'bottom-start', called, fieldListId, fieldId, openAirport, fieldErrorMessage = '', isMultiSelect = true, fieldSize = { xs: 'm', m: 'l' }, } = state;
    const autocompleterFieldId = fieldId;
    const autocompleterListId = fieldListId;
    const dialogAutocompleterFieldId = `${autocompleterFieldId}-dialog`;
    const { fetchData, onDone, setSearchClicked } = actions;
    const { openField, onOpen, onClose, displayType, searchDispatch, searchState, fieldValidity, searchError } = useSearchContext();
    const getAirportOptionCache = useAirportOptionCache();
    const { resourceStrings: { SEARCH_DEPARTURE_FIELD_LABEL, SEARCH_ARRIVAL_FIELD_LABEL, SEARCH_AIRPORTS_FIELD_PLACEHOLDER, SEARCH_DEPARTURE_OPTIONS_TITLE, SEARCH_ARRIVAL_OPTIONS_TITLE, RESULT_COUNT_MESSAGE, RESULTS_COUNT_MESSAGE, HC_CLEAR_ALL, SEARCH_ALREADYSELECTED_CATEGORY_TITLE, }, } = useGlobalContext();
    // DEV ONLY: Count the number of renders of the component
    useRenderCount('AirportAutoCompleter');
    const isDeparture = openAirport === SearchFieldEnum.AIRPORTS;
    const isArrival = openAirport === SearchFieldEnum.ARRIVAL_AIRPORTS;
    const error = isDeparture
        ? ((_a = fieldValidity[SearchFieldEnum.AIRPORTS]) === null || _a === void 0 ? void 0 : _a.error) || false
        : ((_b = fieldValidity[SearchFieldEnum.ARRIVAL_AIRPORTS]) === null || _b === void 0 ? void 0 : _b.error) || false;
    const open = openField === openAirport;
    const prevOpen = usePrevious(open);
    const selectedAirportCodes = ((isDeparture ? searchState.departureAirports : searchState.arrivalAirports) || []).filter(Boolean);
    const selectedAirports = selectedAirportCodes.map((code) => getAirportOptionCache(code));
    const [previouslySelectedAirports, setPreviouslySelectedAirports] = useState([]);
    React.useEffect(() => {
        // On open, we want to display any previously selected airports in our "Already selected" category.
        // We won't add new selections as this will causes a layout shift for the user. However, we will
        // remove any selections which are removed.
        if (open && !prevOpen) {
            setPreviouslySelectedAirports(selectedAirports);
        }
    }, [open, prevOpen, selectedAirports]);
    const summary = useMemo(() => getSelectedOptionSummary(selectedAirports), [selectedAirports]);
    const allAirports = useMemo(() => {
        var _a, _b;
        return loading && previousData
            ? ((_a = previousData === null || previousData === void 0 ? void 0 : previousData.airportAutocompleterResults) === null || _a === void 0 ? void 0 : _a.airports) || []
            : ((_b = data === null || data === void 0 ? void 0 : data.airportAutocompleterResults) === null || _b === void 0 ? void 0 : _b.airports) || [];
    }, [loading, previousData, data]);
    const totalAirports = allAirports.reduce((acc, option) => {
        var _a, _b;
        return acc + ('subAirports' in option ? (_b = (_a = option.subAirports) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0 : 1);
    }, 0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const popup = displayType === SearchDisplayType.POPUP;
    const dialog = displayType === SearchDisplayType.DIALOG;
    const [scrollingPopoverElement, setScrollingPopoverElement] = React.useState(null);
    const withSearchTerm = searchTerm !== '';
    const inputRef = React.useRef(null);
    const handleClearSearchTerm = () => {
        setSearchTerm('');
    };
    const handleFieldChange = (event) => {
        const { value } = event.currentTarget;
        setSearchTerm(value);
        setSearchClicked(false);
    };
    const handleOpen = useCallback(() => {
        onOpen(openAirport);
        handleClearSearchTerm();
    }, [onOpen, openAirport]);
    const handleAdd = useCallback((value) => {
        searchDispatch(Object.assign({ type: isArrival
                ? SearchDispatchActionType.ADD_ARRIVAL_AIRPORTS
                : SearchDispatchActionType.ADD_DEPARTURE_AIRPORTS }, (isArrival
            ? { arrivalAirports: flattenAutocompleterValue(value) }
            : { departureAirports: flattenAutocompleterValue(value) })));
        handleClearSearchTerm();
    }, [isArrival, searchDispatch]);
    const handleRemove = useCallback((value) => {
        searchDispatch(Object.assign({ type: isArrival
                ? SearchDispatchActionType.REMOVE_ARRIVAL_AIRPORTS
                : SearchDispatchActionType.REMOVE_DEPARTURE_AIRPORTS }, (isArrival
            ? { arrivalAirports: flattenAutocompleterValue(value) }
            : { departureAirports: flattenAutocompleterValue(value) })));
        // Also remove from our "Already selected" category.
        setPreviouslySelectedAirports((airports) => airports.filter((airport) => airport.code !== value));
        handleClearSearchTerm();
    }, [isArrival, searchDispatch]);
    const handleRemoveChip = useCallback((value) => (_event) => {
        var _a;
        handleRemove(value);
        // Focus the input after a chip interaction
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [handleRemove]);
    const handleFieldKeyDown = useCallback((event) => {
        if (event.key === 'Backspace') {
            // If there is no text in the field then backspace should delete the last chip.
            if (searchTerm === '') {
                const last = selectedAirports[selectedAirports.length - 1];
                if (last) {
                    handleRemove(last.code);
                }
            }
        }
    }, [handleRemove, searchTerm, selectedAirports]);
    const handleClear = useCallback(() => {
        var _a;
        searchDispatch({
            type: isArrival
                ? SearchDispatchActionType.REMOVE_ALL_ARRIVAL_AIRPORTS
                : SearchDispatchActionType.REMOVE_ALL_DEPARTURE_AIRPORTS,
        });
        // Also clear our "Already selected" category
        setPreviouslySelectedAirports([]);
        // Focus the input after clearing
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [isArrival, searchDispatch]);
    const handleClickOrFocus = () => {
        if (!open) {
            handleOpen();
        }
    };
    const getChips = useCallback(() => {
        return selectedAirports === null || selectedAirports === void 0 ? void 0 : selectedAirports.map((item) => (React.createElement(Chip, { size: "2xs", color: "primary", cornerStyle: "rounded", onDismiss: handleRemoveChip(item.code), tabIndex: -1, key: item === null || item === void 0 ? void 0 : item.code, "data-id": "selected-airport-chip" }, item === null || item === void 0 ? void 0 : item.code)));
    }, [handleRemoveChip, selectedAirports]);
    const getAutocompleterResults = () => {
        // Results loading
        if (!called || loading) {
            return React.createElement(AutocompleterSkeleton, { multiSelect: true });
        }
        // No matches
        if (allAirports.length === 0) {
            return React.createElement(AutocompleterNoMatches, null);
        }
        const categories = [];
        // Previous selections
        if (previouslySelectedAirports.length > 0) {
            categories.push(React.createElement(AutocompleterCategory, { title: SEARCH_ALREADYSELECTED_CATEGORY_TITLE, endDecorator: React.createElement(Anchor, { button: true, onClick: handleClear, size: "xs", weight: "semiBold", truncate: true, "data-id": "airport-autocomp-clear" }, HC_CLEAR_ALL), id: "category-selections", key: "category-selections" },
                React.createElement(AutocompleterList, { labelledById: "category-selections" }, previouslySelectedAirports.reduce((acc, option) => {
                    return [...acc, renderSingleOption(option, false, true)];
                }, []))));
        }
        // Airport options
        categories.push(React.createElement(AutocompleterCategory, { title: isDeparture ? SEARCH_DEPARTURE_OPTIONS_TITLE : SEARCH_ARRIVAL_OPTIONS_TITLE, appendix: pluraliseCount(totalAirports, {
                singular: RESULT_COUNT_MESSAGE,
                plural: RESULTS_COUNT_MESSAGE,
            }), id: "category-airports", key: "category-airports" },
            React.createElement(AutocompleterList, { labelledById: "category-airports" }, allAirports.reduce((acc, option) => {
                if ('subAirports' in option) {
                    const group = renderGroupOption(option);
                    return group ? [...acc, group] : acc;
                }
                if ('code' in option) {
                    return [...acc, renderSingleOption(option, false)];
                }
                return acc;
            }, []))));
        return (React.createElement(AutocompleterResults, { id: autocompleterListId, label: "Suggested airports", spacing: 3 }, categories));
    };
    const autocompleterResults = getAutocompleterResults();
    const commonFieldProps = {
        validationStatus: error ? 'error' : undefined,
        variant: 'filled',
    };
    const fieldError = React.createElement(FieldErrorMessage, { error: error, message: fieldErrorMessage, "data-id": "airport-error-message" });
    const label = isDeparture ? SEARCH_DEPARTURE_FIELD_LABEL : SEARCH_ARRIVAL_FIELD_LABEL;
    const placeholder = isMultiSelect && selectedAirports.length > 0 ? 'Add more' : SEARCH_AIRPORTS_FIELD_PLACEHOLDER;
    const { theme } = useHoneycombTheme();
    const innerPadding = SEARCH_FIELD_POPOVER_INNER_PADDING * theme.spacingUnit;
    return (React.createElement(React.Fragment, null,
        React.createElement(Autocompleter, { fetch: fetchData, onAdd: (value) => handleAdd(value), onRemove: (value) => handleRemove(value), selectedValue: selectedAirports.map((option) => option.code), multiSelect: isMultiSelect, scrollContext: scrollingPopoverElement || undefined },
            React.createElement(React.Fragment, null,
                popup && open && (React.createElement(SearchFieldPopover, { open: open, onClose: () => onClose(), placement: popupPlacement, error: searchError, dynamicHeight: true, variant: "wrapping-field" },
                    React.createElement(PopoverAnchor, null,
                        React.createElement(AutocompleterField, Object.assign({ inputRef: inputRef, id: autocompleterFieldId, listId: autocompleterListId, value: error && searchClicked ? '' : searchTerm, label: label, placeholder: placeholder, onChange: handleFieldChange, onKeyDown: handleFieldKeyDown, autoFocus: true, shrinkLabel: true, size: fieldSize, showClear: withSearchTerm, onButtonClick: handleClearSearchTerm, chips: getChips(), FieldRootProps: Object.assign({ pos: 'relative', transition: SEARCH_FIELD_TRANSITION }, (open && {
                                zIndex: theme.zIndex.popup + 1,
                                w: POPOVER_WIDTH - innerPadding * 2,
                            })), fullWidth: !open }, commonFieldProps))),
                    React.createElement(SearchFieldPopoverPanel, { width: POPOVER_WIDTH, scrollingElementRef: setScrollingPopoverElement }, autocompleterResults))),
                (dialog || !open) && (React.createElement(ButtonField, Object.assign({ value: summary, label: label, onClick: handleClickOrFocus, onFocus: () => popup && handleClickOrFocus(), size: fieldSize, fullWidth: true }, commonFieldProps))),
                fieldError),
            dialog && (React.createElement(FieldDialog, { open: open, onClose: onClose, title: label, headingProps: {
                    component: 'label',
                    htmlFor: dialogAutocompleterFieldId,
                }, ctaProps: {
                    onClick: onDone,
                    label: 'Next',
                    disabled: selectedAirports.length === 0,
                }, resetProps: {
                    onClick: handleClear,
                    label: 'Clear all',
                }, headerContent: React.createElement(React.Fragment, null,
                    React.createElement(AutocompleterField, Object.assign({ inputRef: inputRef, id: dialogAutocompleterFieldId, listId: autocompleterListId, value: searchTerm, placeholder: placeholder, onChange: handleFieldChange, onKeyDown: handleFieldKeyDown, autoFocus: true, showClear: withSearchTerm, onButtonClick: handleClearSearchTerm, fullWidth: true, chips: getChips() }, commonFieldProps)),
                    fieldError) }, autocompleterResults))),
        errorProp && React.createElement("div", null, errorProp.message)));
}
